<!--
  ReplaceModeConfirmModal.vue -- Phase 4.6 persistent confirm for replace
  mode (P4-Q7).

  Card: 2026-06-13 design bundle implementation -- https://trello.com/c/hYlwyKYT
  Pebble: Phase 4.6 BulkApplySetModal (full UI).

  When the user picks "Replace all" + clicks Apply, the parent opens
  this modal. The copy spells out the destructive effect: N decorations
  will be removed across M products; the only recovery path is via the
  Approval Workspace's Update Everywhere / Fork actions on each
  affected artwork (the same path used by the Phase F lifecycle).

  This is intentionally a separate component from
  BulkApplySetModal so the warning UI stays small and the confirm
  emission contract is clear.
-->
<template>
    <div
        v-if="modelValue"
        class="replace-confirm"
        data-testid="replace-mode-confirm-modal"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="replace-confirm-title"
    >
        <div class="replace-confirm__backdrop" @click="onCancel"></div>
        <div class="replace-confirm__panel">
            <h2 id="replace-confirm-title" class="replace-confirm__title">
                Replace all decorations?
            </h2>
            <p class="replace-confirm__copy">
                You are about to remove
                <strong>{{ decorationsToRemove }}</strong>
                decoration{{ decorationsToRemove === 1 ? '' : 's' }}
                from
                <strong>{{ affectedProducts }}</strong>
                product{{ affectedProducts === 1 ? '' : 's' }}.
                This cannot be undone except via Update Everywhere /
                Fork on each affected artwork in the Approval Workspace.
            </p>
            <div class="replace-confirm__actions">
                <button
                    type="button"
                    class="replace-confirm__btn"
                    data-testid="replace-mode-confirm-cancel"
                    @click="onCancel"
                >
                    Cancel
                </button>
                <button
                    type="button"
                    class="replace-confirm__btn replace-confirm__btn--danger"
                    data-testid="replace-mode-confirm-confirm"
                    @click="onConfirm"
                >
                    Replace all
                </button>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'ReplaceModeConfirmModal',
    props: {
        modelValue: {
            type: Boolean,
            required: true,
            default: false,
        },
        decorationsToRemove: {
            type: Number,
            required: true,
            default: 0,
        },
        affectedProducts: {
            type: Number,
            required: true,
            default: 0,
        },
    },
    emits: ['update:modelValue', 'confirm', 'cancel'],
    methods: {
        onConfirm() {
            this.$emit('confirm')
        },
        onCancel() {
            this.$emit('cancel')
            this.$emit('update:modelValue', false)
        },
    },
}
</script>

<style scoped>
.replace-confirm {
    position: fixed;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 300;
}
.replace-confirm__backdrop {
    position: absolute;
    inset: 0;
    background: rgba(15, 23, 42, 0.6);
}
.replace-confirm__panel {
    position: relative;
    background: #fff;
    border-radius: 8px;
    box-shadow: 0 10px 30px rgba(15, 23, 42, 0.3);
    padding: 24px;
    width: min(480px, 90vw);
    z-index: 1;
    border-top: 4px solid #dc2626;
}
.replace-confirm__title {
    margin: 0 0 12px;
    color: #991b1b;
    font-size: 18px;
}
.replace-confirm__copy {
    margin: 0 0 16px;
    font-size: 14px;
    color: #1e293b;
    line-height: 1.45;
}
.replace-confirm__actions {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
}
.replace-confirm__btn {
    appearance: none;
    background: #fff;
    border: 1px solid #cbd5e1;
    border-radius: 4px;
    padding: 8px 14px;
    cursor: pointer;
    font-size: 13px;
}
.replace-confirm__btn--danger {
    background: #dc2626;
    border-color: #dc2626;
    color: #fff;
}
.replace-confirm__btn--danger:hover {
    background: #b91c1c;
}
</style>
